import React, { useEffect, useState, useMemo } from "react";
import { Table, Button, Pagination, Checkbox } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { getPractitionersList } from "../../api/Practitioner/index";
import { transformPractitionerFiltersForAPI } from "../../../../utils";
import { ChevronRight } from "lucide-react";
import useIsMobile from "../shared/Mobile-Responsive";
import "antd/dist/reset.css";
import "./index.css";

// Badge styling
const statusBadge = (status) => {
  const base = "px-2.5 py-0.5 rounded-full text-[11px] font-semibold font-poppins";
  if (status === "ACTIVE")
    return <span className={`${base} bg-[#ECFDF5] text-[#059669]`}>ACTIVE</span>;
  return <span className={`${base} bg-[#F1F5F9] text-[#64748B]`}>INACTIVE</span>;
};

// Format availability days
const formatAvailabilityDays = (availableDays) => {
  if (!availableDays || availableDays.length === 0) {
    return "Not Available";
  }
  return availableDays.join(", ");
};

// Format languages or specialization info
const formatLanguagesOrSpecialization = (languages, specialization) => {
  if (languages) {
    return languages;
  }
  if (specialization) {
    return specialization;
  }
  return "Not specified";
};

// Mobile Practitioner Card Component
const PractitionerCard = ({ practitioner }) => {
  return (
    <div className="bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl p-4 mb-3 shadow-sm font-poppins">
      {/* Checkbox */}
      <div className="flex items-center justify-between mb-3">
        <Checkbox className="flex-shrink-0" />
        <div className="text-xs text-[#64748B] font-medium">ID: {practitioner.pcUserId}</div>
      </div>

      <div className="space-y-3">
        <div className="flex justify-between">
          <span className="text-[11px] font-semibold text-[#64748B] uppercase tracking-wide">NAME</span>
          <span className="text-[13px] font-medium text-[#171717]">{practitioner.name}</span>
        </div>

        <div className="flex justify-between">
          <span className="text-[11px] font-semibold text-[#64748B] uppercase tracking-wide">SPECIALIZATION</span>
          <span className="text-[13px] font-medium text-[#171717]">{practitioner.specialization || "Not specified"}</span>
        </div>

        <div className="flex justify-between">
          <span className="text-[11px] font-semibold text-[#64748B] uppercase tracking-wide">LANGUAGES</span>
          <span className="text-[13px] font-medium text-[#171717]">{formatLanguagesOrSpecialization(practitioner.languages, practitioner.specialization)}</span>
        </div>

        <div className="flex justify-between">
          <span className="text-[11px] font-semibold text-[#64748B] uppercase tracking-wide">AVAILABILITY</span>
          <span className="text-[13px] font-medium text-[#171717]">{formatAvailabilityDays(practitioner.availableDays)}</span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-[11px] font-semibold text-[#64748B] uppercase tracking-wide">STATUS</span>
          {statusBadge(practitioner.status)}
        </div>

        <div className="flex justify-between">
          <span className="text-[11px] font-semibold text-[#64748B] uppercase tracking-wide">CONSULTATIONS</span>
          <span className="text-[13px] font-medium text-[#171717]">{practitioner.numberOfConsultations}</span>
        </div>
      </div>

      <div className="border-t-[1px] border-solid border-x-0 border-b-0 border-[#F1F5F9] mt-4 pt-3 flex justify-end">
        <button
          onClick={() => (window.location.href = `/practitioner-detail/${practitioner.pcUserId}`)}
          className="flex items-center justify-center w-[26px] h-[26px] rounded-full bg-[#F1F5F9] hover:bg-[#E2E8F0] border-0 transition-colors"
        >
          <ChevronRight size={14} className="text-[#64748B]" />
        </button>
      </div>
    </div>
  );
};

// Table columns
const columns = [
  {
    title: "",
    dataIndex: "checkbox",
    key: "checkbox",
    render: () => null, // checkbox handled by rowSelection
  },
  {
    title: "P_ID",
    dataIndex: "pcUserId",
    key: "pcUserId",
  },
  {
    title: "NAME",
    dataIndex: "name",
    key: "name",
  },
  {
    title: "SPECIALIZATION",
    dataIndex: "specialization",
    key: "specialization",
    render: (specialization) => specialization || "Not specified",
  },
  {
    title: "LANGUAGES",
    dataIndex: "languages",
    key: "languages",
    render: (languages, record) => formatLanguagesOrSpecialization(languages, record.specialization),
  },
  {
    title: "AVAILABILITY DAYS",
    dataIndex: "availableDays",
    key: "availableDays",
    render: (availableDays) => formatAvailabilityDays(availableDays),
  },
  {
    title: "STATUS",
    dataIndex: "status",
    key: "status",
    render: (status) => statusBadge(status),
  },
  {
    title: "CONSULTATIONS",
    dataIndex: "numberOfConsultations",
    key: "numberOfConsultations",
    render: (count) => `${count}`,
  },
  {
    title: "",
    key: "action",
    render: (_, record) => (
      <button
        type="button"
        className="flex items-center justify-center w-[26px] h-[26px] rounded-full bg-[#F1F5F9] hover:bg-[#E2E8F0] border-0 transition-colors"
        onClick={() => (window.location.href = `/practitioner-detail/${record.pcUserId}`)}
      >
        <ChevronRight size={14} className="text-[#64748B]" />
      </button>
    ),
  },
];

const DoctorsTable = ({ searchTerm, appliedFilters, currentPage, setCurrentPage, pageSize, setPageSize, onSelectedRowsChange, setExportData, selectedRows, sortConfig }) => {
  const dispatch = useDispatch();
  const [selectedRowKeys, setSelectedRowKeys] = useState(selectedRows || []);
  const isMobile = useIsMobile();

  // Redux state
  const {
    data: practitionersList,
    loading: listLoading,
    error: listError,
    totalElements,
    totalPages,
    currentPage: apiCurrentPage
  } = useSelector(state => state.practitioners.list);

  // Load practitioners list on mount and filter changes
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Build filters for API call
      const combinedFilters = {};

      // Add search term as name filter
      if (searchTerm && searchTerm.trim()) {
        combinedFilters.name = searchTerm.trim();
      }

      // Add advanced filters using utility function
      if (appliedFilters) {
        const transformedFilters = transformPractitionerFiltersForAPI(appliedFilters);
        Object.assign(combinedFilters, transformedFilters);
      }

      dispatch(getPractitionersList({
        page: currentPage - 1, // API uses 0-based indexing
        size: pageSize,
        filters: combinedFilters
      }));
    }
  }, [dispatch, currentPage, pageSize, searchTerm, appliedFilters]);

  // Transform API data for table display with client-side sorting
  const transformedData = useMemo(() => {
    if (!practitionersList || !Array.isArray(practitionersList)) return [];

    let data = practitionersList.map((item, index) => ({
      key: item.pcUserId || `practitioner-${index}`,
      pcUserId: item.pcUserId,
      name: item.name,
      specialization: item.specialization,
      languages: item.languages,
      availableDays: item.availableDays,
      status: item.status,
      numberOfConsultations: item.numberOfConsultations || 0,
    }));

    // Apply client-side sorting if sortConfig is provided
    if (sortConfig && sortConfig.field && sortConfig.order) {
      data = [...data].sort((a, b) => {
        const field = sortConfig.field;
        let valA = a[field];
        let valB = b[field];

        // Handle null/undefined values
        if (!valA) valA = "";
        if (!valB) valB = "";

        // Convert to lowercase for string comparison
        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();

        if (sortConfig.order === 'asc') {
          return valA > valB ? 1 : valA < valB ? -1 : 0;
        } else {
          return valB > valA ? 1 : valB < valA ? -1 : 0;
        }
      });
    }

    // Update export data whenever transformed data changes
    if (setExportData) {
      setExportData(data);
    }

    return data;
  }, [practitionersList, setExportData, sortConfig]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
      if (onSelectedRowsChange) {
        onSelectedRowsChange(selectedRowKeys);
      }
      console.log("Selected:", selectedRowKeys, selectedRows);
    },
  };

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    if (size !== pageSize) {
      setPageSize(size);
    }
  };

  // Loading state
  if (listLoading) {
    return (
      <div className="p-2 rounded-lg overflow-x-auto font-poppins">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563EB] mx-auto"></div>
          <p className="mt-2 text-sm text-[#64748B]">Loading practitioners...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (listError) {
    return (
      <div className="p-2 rounded-lg overflow-x-auto font-poppins">
        <div className="text-center py-8">
          <p className="text-[#DC2626] mb-4">Error: {listError}</p>
          <Button
            onClick={() => dispatch(getPractitionersList({
              page: currentPage - 1,
              size: pageSize,
              filters: {}
            }))}
            className="btn-gradient h-9 px-[14px] text-[13px] font-semibold rounded-xl border-0"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Empty state
  if (!transformedData.length) {
    return (
      <div className="p-2 rounded-lg overflow-x-auto font-poppins">
        <div className="text-center py-8">
          <p className="text-sm text-[#64748B]">No practitioners found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 rounded-lg overflow-x-auto font-poppins">
      {/* Desktop View - Hidden on mobile */}
      <div className="hidden md:block">
        <Table
          rowSelection={{
            type: "checkbox",
            ...rowSelection,
          }}
          columns={columns}
          dataSource={transformedData}
          pagination={false}
          bordered={false}
          className="font-poppins custom-table"
          rowClassName={() => "bg-white text-[#334155] text-[13px] hover:bg-[#F8FAFC] transition"}
        />
      </div>

      {/* Mobile View - Hidden on desktop */}
      <div className="block md:hidden">
        <div className="p-2">
          {transformedData.length > 0 ? (
            transformedData.map((practitioner) => (
              <PractitionerCard key={practitioner.key} practitioner={practitioner} />
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-sm text-[#64748B]">No practitioners found</p>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      <div className={`flex ${isMobile ? "flex-col pt-3 pb-3" : "flex-row py-3"} justify-between items-center mt-4 px-2 gap-4 border-t-[1px] border-solid border-x-0 border-b-0 border-[#E2E8F0]`}>
        <div className={`${isMobile ? "text-xs" : "text-sm"} text-[#64748B] font-poppins`}>
          Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalElements)} of {totalElements} entries
        </div>
        <Pagination
          current={currentPage}
          total={totalElements}
          pageSize={pageSize}
          showSizeChanger={true}
          showQuickJumper={!isMobile}
          onChange={handlePageChange}
          showTotal={false}
          pageSizeOptions={['10', '20', '50', '100']}
          className="practitioner-pagination"
          size={isMobile ? "small" : "default"}
        />
      </div>
    </div>
  );
};

export default DoctorsTable;
